import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import PersonIcon from "@mui/icons-material/Person";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import CategoryIcon from "@mui/icons-material/Category";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";

const CURRENT_YEAR = new Date().getFullYear();

// Column layout grouped like a professional report
const DEFAULT_COLUMNS = [
  // Identity
  {
    field: "person_code",
    label: "Code",
    visible: true,
    sortable: true,
    filterable: true,
    width: 120,
  },
  {
    field: "full_name",
    label: "Name",
    visible: true,
    sortable: true,
    filterable: true,
    width: 200,
  },
  {
    field: "person_type_label",
    label: "Type",
    visible: true,
    sortable: true,
    filterable: true,
    width: 110,
  },

  // Calendar overview
  { field: "year", label: "Year", visible: true, sortable: true, width: 90 },
  { field: "month", label: "Month", visible: true, sortable: true, width: 90 },
  {
    field: "total_days_in_month",
    label: "Total Days",
    visible: true,
    sortable: true,
    width: 110,
  },
  {
    field: "workdays_in_month",
    label: "Workdays",
    visible: true,
    sortable: true,
    width: 110,
  },
  {
    field: "holidays_in_month",
    label: "Holidays",
    visible: true,
    sortable: true,
    width: 110,
  },
  {
    field: "weekoff_in_month",
    label: "Weekoffs",
    visible: false,
    sortable: true,
    width: 110,
  },
  {
    field: "off_days_in_month",
    label: "Off Days",
    visible: false,
    sortable: true,
    width: 110,
  },

  // Attendance summary
  {
    field: "working_days",
    label: "Present Days",
    visible: true,
    sortable: true,
    width: 120,
  },
  {
    field: "absent_days",
    label: "Absent Days",
    visible: true,
    sortable: true,
    width: 120,
  },

  // Leave summary
  {
    field: "leave_days",
    label: "Total Leave Days",
    visible: true,
    sortable: true,
    width: 140,
  },
  {
    field: "casual_leaves",
    label: "Casual",
    visible: true,
    sortable: true,
    width: 100,
  },
  {
    field: "medical_leaves",
    label: "Medical",
    visible: true,
    sortable: true,
    width: 100,
  },
  {
    field: "earned_leaves",
    label: "Earned",
    visible: true,
    sortable: true,
    width: 100,
  },
  {
    field: "compensatory_off_leaves",
    label: "Comp Off Used",
    visible: true,
    sortable: true,
    width: 140,
  },
  {
    field: "approved_leave_days",
    label: "Approved",
    visible: true,
    sortable: true,
    width: 120,
  },
  {
    field: "unapproved_leave_days",
    label: "Unapproved",
    visible: true,
    sortable: true,
    width: 130,
  },
  {
    field: "compensatory_off_earned",
    label: "Comp Off Earned",
    visible: false,
    sortable: true,
    width: 150,
  },

  // Deviations & overtime
  {
    field: "late_entries",
    label: "Late Entries",
    visible: true,
    sortable: true,
    width: 120,
  },
  {
    field: "break_time_exceed_entries",
    label: "Break Exceeds",
    visible: false,
    sortable: true,
    width: 130,
  },
  {
    field: "early_exits",
    label: "Early Exits",
    visible: true,
    sortable: true,
    width: 110,
  },
  {
    field: "total_overtime_minutes",
    label: "OT (min)",
    visible: true,
    sortable: true,
    width: 110,
  },
  {
    field: "expected_shift_minutes",
    label: "Expected (min)",
    visible: true,
    sortable: true,
    width: 130,
  },
  {
    field: "actual_work_minutes",
    label: "Actual (min)",
    visible: true,
    sortable: true,
    width: 130,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 50,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

function AttendanceMonthlyReportList({
  defaultPersonTypeId = "",
  heading = "Monthly Attendance Report",
  routePath = "/attendance/monthly-report",
}) {
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [columns] = useState(DEFAULT_COLUMNS);

  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [personTypeId, setPersonTypeId] = useState(defaultPersonTypeId);

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("full_name");
  const [sortDirection, setSortDirection] = useState("asc");

  const filteredAndSortedRows = useMemo(() => {
    let data = [...rows];

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      data = data.filter((row) => {
        const name = (row.full_name || "").toLowerCase();
        const code = (row.person_code || "").toLowerCase();
        const type = (row.person_type_label || "").toLowerCase();
        return (
          name.includes(term) ||
          code.includes(term) ||
          type.includes(term)
        );
      });
    }

    data.sort((a, b) => {
      const dir = sortDirection === "desc" ? -1 : 1;
      const av = a[sortField];
      const bv = b[sortField];

      if (av === undefined || av === null) return 1 * dir;
      if (bv === undefined || bv === null) return -1 * dir;

      if (typeof av === "number" && typeof bv === "number") {
        return av === bv ? 0 : av > bv ? dir : -dir;
      }

      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as === bs) return 0;
      return as > bs ? dir : -dir;
    });

    return data;
  }, [rows, search, sortField, sortDirection]);

  // Simple KPI summary for the current result set
  const kpi = useMemo(() => {
    if (!filteredAndSortedRows.length) {
      return {
        totalPersons: 0,
        avgWorkingDays: 0,
        avgAbsentDays: 0,
        totalLeaveDays: 0,
      };
    }

    const totalPersons = filteredAndSortedRows.length;
    const sumWorking = filteredAndSortedRows.reduce(
      (sum, r) => sum + (Number(r.working_days) || 0),
      0
    );
    const sumAbsent = filteredAndSortedRows.reduce(
      (sum, r) => sum + (Number(r.absent_days) || 0),
      0
    );
    const sumLeave = filteredAndSortedRows.reduce(
      (sum, r) => sum + (Number(r.leave_days) || 0),
      0
    );

    return {
      totalPersons,
      avgWorkingDays: Number.isFinite(sumWorking / totalPersons)
        ? (sumWorking / totalPersons).toFixed(1)
        : 0,
      avgAbsentDays: Number.isFinite(sumAbsent / totalPersons)
        ? (sumAbsent / totalPersons).toFixed(1)
        : 0,
      totalLeaveDays: sumLeave.toFixed(1),
    };
  }, [filteredAndSortedRows]);

  useEffect(() => {
    setPersonTypeId(defaultPersonTypeId);
  }, [defaultPersonTypeId]);

  const loadReport = useCallback(async () => {
    if (!org?.organization_id) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("year", String(year));
      params.set("month", String(month));
      if (personTypeId) {
        params.set("person_type_id", String(personTypeId));
      }

      const response = await fetch(
        `${MAIN_URL}/api/organizations/${org.organization_id}/attendance/monthly-report?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || "Failed to load report");
      }

      const data = Array.isArray(result.data) ? result.data : [];
      setRows(
        data.map((item, index) => ({
          ...item,
          id: item.organization_person_id ?? index,
        }))
      );

      setPaginationData({
        ...DEFAULT_PAGINATION,
        total: data.length,
        totalPages: 1,
      });
    } catch (error) {
      console.error("Failed to load monthly attendance report", error);
      toast.error(error.message || "Failed to load monthly attendance report");
      setRows([]);
      setPaginationData(DEFAULT_PAGINATION);
    } finally {
      setLoading(false);
    }
  }, [org?.organization_id, year, month, personTypeId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleSortChange = useCallback((field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  }, []);

  const handleYearChange = (e) => {
    setYear(Number(e.target.value) || CURRENT_YEAR);
  };

  const handleMonthChange = (e) => {
    setMonth(Number(e.target.value) || 1);
  };

  const handlePersonTypeChange = (e) => {
    setPersonTypeId(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  return (
    <>
      <Layout4
        loading={loading}
        heading={heading}
        btnName={null}
        Data={filteredAndSortedRows}
        tableHeaders={[
          { name: "Name", value_key: "full_name", textStyle: "capitalize" },
          { name: "Code", value_key: "person_code" },
          { name: "Type", value_key: "person_type_label" },
          { name: "Working Days", value_key: "working_days" },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          heading,
          heading,
          heading,
          heading,
        ]}
        Route={routePath}
        setData={setRows}
      >
        {/* Report filters / header */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            marginBottom: "1rem",
            alignItems: "flex-end",
          }}
        >
          <div>
            <label
              style={{ display: "block", fontSize: 12, fontWeight: 600 }}
            >
              Year
            </label>
            <input
              type="number"
              value={year}
              onChange={handleYearChange}
              style={{ padding: "4px 8px", minWidth: 100 }}
            />
          </div>
          <div>
            <label
              style={{ display: "block", fontSize: 12, fontWeight: 600 }}
            >
              Month
            </label>
            <select
              value={month}
              onChange={handleMonthChange}
              style={{ padding: "4px 8px", minWidth: 140 }}
            >
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              style={{ display: "block", fontSize: 12, fontWeight: 600 }}
            >
              Person Type
            </label>
            <select
              value={personTypeId}
              onChange={handlePersonTypeChange}
              style={{ padding: "4px 8px", minWidth: 140 }}
            >
              <option value="">All</option>
              <option value="1">Employee</option>
              <option value="2">Intern</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label
              style={{ display: "block", fontSize: 12, fontWeight: 600 }}
            >
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by name, code, type"
              style={{ width: "100%", padding: "4px 8px" }}
            />
          </div>
          <div>
            <button
              onClick={loadReport}
              style={{
                padding: "6px 14px",
                backgroundColor: "#1976d2",
                border: "none",
                borderRadius: 4,
                color: "white",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Run Report
            </button>
          </div>
        </div>

        {/* Report period + total */}
        <div
          style={{
            marginBottom: "0.75rem",
            fontSize: 13,
            color: "#555",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            rowGap: "0.25rem",
          }}
        >
          <div>
            Report period:&nbsp;
            <strong>{year}</strong>, month <strong>{month}</strong>
            {personTypeId && (
              <>
                &nbsp;&mdash;&nbsp;type:&nbsp;
                <strong>
                  {personTypeId === "1" ? "Employee" : "Intern"}
                </strong>
              </>
            )}
          </div>
          <div>
            Total persons:&nbsp;
            <strong>{filteredAndSortedRows.length}</strong>
          </div>
        </div>

        {/* KPI summary cards */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <div
            style={{
              flex: "1 1 200px",
              padding: "10px 12px",
              borderRadius: 6,
              background: "#f5f5f5",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                color: "#777",
              }}
            >
              Total Persons
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {kpi.totalPersons}
            </div>
          </div>

          <div
            style={{
              flex: "1 1 200px",
              padding: "10px 12px",
              borderRadius: 6,
              background: "#f5f5f5",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                color: "#777",
              }}
            >
              Avg Working Days
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {kpi.avgWorkingDays}
            </div>
          </div>

          <div
            style={{
              flex: "1 1 200px",
              padding: "10px 12px",
              borderRadius: 6,
              background: "#f5f5f5",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                color: "#777",
              }}
            >
              Avg Absent Days
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {kpi.avgAbsentDays}
            </div>
          </div>

          <div
            style={{
              flex: "1 1 200px",
              padding: "10px 12px",
              borderRadius: 6,
              background: "#f5f5f5",
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                color: "#777",
              }}
            >
              Total Leave Days
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {kpi.totalLeaveDays}
            </div>
          </div>
        </div>
      </Layout4>

      <TableDataGeneric
        tableName="attendance_monthly_report"
        primaryKey="organization_person_id"
        heading={heading}
        data={filteredAndSortedRows}
        sortname={sortField}
        showActions={false}
        Route={routePath}
        onSortChange={handleSortChange}
        token={localStorage.getItem("token")}
        configss={columns}
        paginationData={paginationData}
      />
    </>
  );
}

export default AttendanceMonthlyReportList;

